import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { loadStripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { CardItem } from '@web/app/features/my-page/components/CardItem';
import { ErrorCard, useDialogStore, useGetBillingState, useGetInvoiceState } from '@web/app/shared';
import { ChevronRight, Plus } from 'lucide-react';
import moment from 'moment';

import { useBillings } from '@codes/billings';
import { useInvoices } from '@codes/invoices';
import { useLoader } from '@codes/loader';
import { BulkCardUpdateBody, cardQueryKeys, useBulkUpdateCards, useCards, useDeleteCard } from '@codes/payments';
import { getDollarByPrice, useBillingDate } from '@codes/react-utils';
import { ListResult } from '@codes/shared';
import { Button, Dialog, DialogContent, DialogTrigger, Skeleton } from '@codes/ui-kit';
import { formatDate } from '@codes/utils';

import { getInvoiceMonths } from '../../business';
import { BillingsDetail } from '../../components/billings-detail';
import { InvoicesDetail } from '../../components/invoices-detail';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

export const BillingsPage = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { setIsLoading } = useLoader();

    const { setCardDialog } = useDialogStore(state => state);

    const getInvoiceState = useGetInvoiceState();
    const getBillingState = useGetBillingState();

    const { estimatedBillingMs } = useBillingDate();

    const [invoiceFilterKeyword, setInvoiceFilterKeyword] = useState('all');

    const {
        data: cardsData,
        isLoading: isCardsFetching,
        isError,
        error,
        refetch,
    } = useCards({ stereo: 'stripe', view: 'mine' });

    const defaultCard = useMemo(() => {
        if (!cardsData || !cardsData.list || cardsData.list.length === 0) {
            return null;
        }
        const card = cardsData.list.find(card => card.isDefault);

        if (!card) {
            return cardsData.list[0];
        }

        return card;
    }, [cardsData]);

    const { data: billingsData, isLoading: isBillingsFetching } = useBillings({
        view: 'mine',
        limit: 100,
    });

    const { data: invoicesData, isLoading: isInvoicesFetching } = useInvoices({ view: 'mine', limit: 100 });

    const filteredInvoices = useMemo(() => {
        if (!invoicesData || !invoicesData.data) {
            return [];
        }

        if (invoiceFilterKeyword === 'all' || !invoiceFilterKeyword) {
            return invoicesData.data;
        }

        return invoicesData.data.filter(invoice => {
            return formatDate(invoice.startedAt, 'YYYY.MM') === invoiceFilterKeyword;
        });
    }, [invoicesData, invoiceFilterKeyword]);

    const haveBillings = billingsData && billingsData.total !== 0;

    const invoicedBillingIds = invoicesData?.data
        ? invoicesData.data.map(invoice => invoice.bids as string[]).flat()
        : [];

    const filteredBillings = useMemo(() => {
        if (!haveBillings) {
            return [];
        }
        return billingsData.data.filter(
            billing =>
                !invoicedBillingIds.includes(billing.id as string) &&
                formatDate(billing.estimatedBillingMs, 'YYYY.MM') === formatDate(estimatedBillingMs, 'YYYY.MM')
        );
    }, [haveBillings, billingsData, invoicedBillingIds, estimatedBillingMs]);

    const haveFilteredBillings = filteredBillings.length !== 0;

    const billingTotalPrice = useMemo(() => {
        if (!haveFilteredBillings) {
            return 0;
        }
        const totalPrice = filteredBillings.reduce((cur, billing) => {
            if (billing.totalPrice$?.currency === 'USD') {
                return cur + (billing.totalPrice || 0);
            }

            return cur + getDollarByPrice(billing.totalPrice || 0, billing.totalPrice$?.currency);
        }, 0);
        return totalPrice?.toLocaleString();
    }, [haveFilteredBillings, filteredBillings]);

    const haveInvoices = invoicesData && invoicesData.total !== 0;

    const invoiceMonths = useMemo(() => {
        if (!haveInvoices) {
            return [];
        }

        return getInvoiceMonths(invoicesData.data);
    }, [haveInvoices, invoicesData]);

    const bulkUpdateCards = useBulkUpdateCards();
    const deleteCard = useDeleteCard();

    const handleDefaultCardClick = async (card: CardView) => {
        const bulkCardBody: BulkCardUpdateBody = {
            list: [
                ...(cardsData?.list || []).map(origin =>
                    origin.id === card.id
                        ? { ...card, isDefault: true }
                        : {
                              ...origin,
                              isDefault: false,
                          }
                ),
            ],
        };

        setIsLoading(true);
        try {
            await bulkUpdateCards.mutateAsync(bulkCardBody, {
                onSuccess: async (updatedCards: CardView[]) => {
                    queryClient.setQueryData(cardQueryKeys.list({ stereo: 'stripe', view: 'mine' }), (oldData: any) => {
                        if (!oldData) {
                            return oldData;
                        }

                        return {
                            ...oldData,
                            data: updatedCards,
                            list: updatedCards,
                        };
                    });
                    toast({ description: `Successfully updated.`, duration: 2000 });
                },
                onError: error => {
                    console.error('Failed to update card:', error);
                    toast({
                        description: `Failed to update! ${error.toString()}`,
                        variant: 'destructive',
                        duration: 2000,
                    });
                },
            });
        } catch (error) {
            toast({
                description: `Failed to update card: ${error.message}`,
                variant: 'destructive',
                duration: 2000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteCard = async (card: CardView) => {
        setIsLoading(true);
        try {
            await deleteCard.mutateAsync(card.id as string, {
                onSuccess: async (deleted: CardView) => {
                    queryClient.setQueryData<ListResult<CardView>>(
                        cardQueryKeys.list({ stereo: 'stripe', view: 'mine' }),
                        oldData => {
                            if (!oldData) {
                                return oldData;
                            }
                            return {
                                ...oldData,
                                list: oldData.list.filter(c => c.id !== deleted.id),

                                total: (oldData.total || 1) - 1,
                            };
                        }
                    );
                    toast({ description: t('myPage.cardDeletionComplete'), duration: 2000 });
                },
            });
        } catch (error) {
            toast({
                description: `Failed to delete card: ${error.message}`,
                variant: 'destructive',
                duration: 2000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const openCardDialog = () => {
        setCardDialog(true);
    };

    return (
        <div className="max-w-[500px] mx-auto flex flex-col gap-[26px]">
            {/* TODO: 결제 예정 */}
            <div>
                <div className="w-full text-[18px] font-medium mb-4">{t('myPage.paymentScheduled')}</div>
                <div className="flex flex-col gap-2">
                    {isBillingsFetching && (
                        <div className="flex flex-col gap-y-2">
                            <Skeleton className="w-full flex justify-center items-center min-h-8 rounded-lg"></Skeleton>
                            <Skeleton className="w-full flex justify-center items-center min-h-24 rounded-lg"></Skeleton>
                        </div>
                    )}
                    {!isBillingsFetching &&
                        (haveFilteredBillings ? (
                            <Card className="rounded-lg flex flex-col gap-[10px] dark:bg-[#2e2e2e]">
                                <div>
                                    <Dialog>
                                        <DialogTrigger asChild className="">
                                            <div className="flex items-center justify-between text-sm">
                                                <div className="font-medium">
                                                    {formatDate(estimatedBillingMs, 'MMMM')}
                                                    's {t('myPage.paymentScheduled')}
                                                </div>

                                                <div className="flex justify-center">
                                                    <button className="text-sm text-sub-title flex items-center">
                                                        {t('myPage.viewDetails')}
                                                        <ChevronRight className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-[500px] p-0" aria-describedby={undefined}>
                                            <BillingsDetail billings={filteredBillings} card={defaultCard} />
                                        </DialogContent>
                                    </Dialog>
                                </div>

                                <div className="text-[18px] text-point font-medium flex items-center justify-between">
                                    <div>{t('myPage.totalExpectedAmount')}</div>
                                    <div>${billingTotalPrice}</div>
                                </div>
                            </Card>
                        ) : (
                            <div className="text-placeholder text-center py-[12px]">
                                There is no scheduled recurring payment history.
                            </div>
                        ))}
                </div>
            </div>
            {/* 결제 내역 */}
            <div>
                <div className="w-full text-[18px] font-medium mb-[10px]">{t('myPage.paymentHistory')}</div>
                <div className="flex flex-col gap-2">
                    {isInvoicesFetching && (
                        <div className="flex flex-col gap-y-2">
                            <Skeleton className="w-full flex justify-center items-center min-h-10 rounded-lg"></Skeleton>
                            <Skeleton className="w-full flex justify-center items-center min-h-24 rounded-lg"></Skeleton>
                        </div>
                    )}

                    {!isInvoicesFetching &&
                        (haveInvoices ? (
                            <>
                                <Select
                                    value={invoiceFilterKeyword}
                                    onValueChange={v => setInvoiceFilterKeyword(v)}
                                    defaultValue="all"
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('myPage.all')}</SelectItem>
                                        {invoiceMonths.map(month => (
                                            <SelectItem value={month} key={month}>
                                                {month}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {filteredInvoices.map(invoice => (
                                    <Card key={invoice.id} className="rounded-lg flex flex-col gap-1 dark:bg-[#2e2e2e]">
                                        <div className="flex items-center justify-between text-sm mb-[6px]">
                                            <div className="font-medium">
                                                {moment(invoice.startedAt).format('MMMM')}'s{' '}
                                                {t('myPage.paymentHistory')}
                                            </div>
                                            <Dialog>
                                                <DialogTrigger asChild className="flex justify-center">
                                                    <button className="text-sm text-sub-title flex items-center">
                                                        {t('myPage.viewDetails')}
                                                        <ChevronRight className="w-4 h-4" />
                                                    </button>
                                                </DialogTrigger>
                                                <DialogContent
                                                    className="max-w-[500px] p-0"
                                                    aria-describedby={undefined}
                                                >
                                                    <InvoicesDetail invoice={invoice} card={defaultCard} />
                                                </DialogContent>
                                            </Dialog>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <div>{t('myPage.paymentDate')}</div>
                                            <div className="font-medium">
                                                {moment(invoice.startedAt).format('YYYY. MM. DD')}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <div>{t('myPage.status')}</div>
                                            <div className="font-medium">
                                                {invoice.state$
                                                    ? getInvoiceState(invoice.state$)
                                                    : getBillingState(invoice.state)}
                                            </div>
                                        </div>
                                        <div className="text-[18px] text-point font-medium flex items-center justify-between">
                                            <div>{t('myPage.totalAmount')}</div>
                                            <div>
                                                $
                                                {getDollarByPrice(
                                                    invoice.totalAmount || 0,
                                                    invoice.totalAmount$?.currency
                                                )?.toLocaleString()}
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </>
                        ) : (
                            <div className="text-placeholder text-center py-[12px]">There is no payment history.</div>
                        ))}
                </div>
            </div>
            {/* TODO: 결제 수단 */}
            <div>
                <div className="w-full text-[18px] font-medium mb-4">{t('myPage.paymentMethod')}</div>
                <div className="flex flex-col gap-2">
                    {isError && <ErrorCard error={error} resetError={() => refetch()} />}

                    {isCardsFetching && (
                        <div className="flex flex-col gap-y-8">
                            <Skeleton className="w-full flex justify-center items-center min-h-20 rounded-lg"></Skeleton>
                            <Skeleton className="w-full flex justify-center items-center min-h-12 rounded-lg"></Skeleton>
                        </div>
                    )}
                    {cardsData?.total === 0 && (
                        <Card className="h-[172px] rounded-lg flex flex-col items-center justify-center text-center whitespace-pre-wrap dark:bg-[#2e2e2e]">
                            <div className="text-placeholder">{t('myPage.noPaymentMethod')}</div>
                            <button
                                onClick={openCardDialog}
                                className="w-10 h-10 rounded-full bg-badge flex items-center justify-center mt-6"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </Card>
                    )}
                    {cardsData?.list.map(card => (
                        <CardItem
                            key={card.id}
                            card={card}
                            onDefaultCardClick={handleDefaultCardClick}
                            onDeleteCard={handleDeleteCard}
                        />
                    ))}
                    {cardsData?.total !== 0 && (
                        <Button
                            onClick={openCardDialog}
                            size="lg"
                            className="bg-badge text-sub-title w-full mt-[34px] flex gap-1 hover:bg-badge"
                        >
                            <Plus className="w-4 h-4" />
                            {t('myPage.registerCard')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
